import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SparklesIcon, CheckCircleIcon, ClockIcon, ChevronRightIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { config } from '../config/environment';
import { EventTypeOption } from '../components/EventTypePicker';
import SlotPicker from '../components/SlotPicker';

type Step =
  | 'loading' | 'not_found'
  | 'picker'
  | 'slots' | 'confirm' | 'session_pay' | 'confirmed'
  | 'pkg_details' | 'pkg_pay' | 'pkg_confirmed';

interface PublicPackage {
  id: string;
  name: string;
  description: string | null;
  package_type: string;
  total_price_cents: number | null;
  currency: string;
  setup_fee_cents: number | null;
  allow_installments: boolean | null;
  installment_count: number | null;
  installment_amount_cents: number | null;
  installment_frequency: string | null;
  duration_value: number | null;
  duration_unit: string | null;
  payment_method: string | null;
  monthly_price_cents: number | null;
}

interface PublicEventType extends EventTypeOption {
  billing_type?: string;
  default_price_cents?: number | null;
  default_currency?: string | null;
}

interface BookingInfo {
  coach_id: string;
  coach_name: string;
  scheduling_type: 'google' | 'calendly' | 'none';
  enforce_payment_sessions: boolean;
  enforce_payment_packages: boolean;
  event_types: PublicEventType[];
  packages: PublicPackage[];
}

interface EnrollResult {
  enrollment_id: string;
  payment_id: string | null;
  amount_cents: number;
  currency: string;
  client_secret: string | null;
  stripe_publishable_key: string | null;
  stripe_account_id: string | null;
  is_new_client: boolean;
  package_name: string;
  payment_method: string | null;
}

function fmt(cents: number | null, currency: string) {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
}

function pkgPriceSummary(pkg: PublicPackage): string {
  if (pkg.package_type === 'ongoing_monthly' && pkg.monthly_price_cents) {
    return `${fmt(pkg.monthly_price_cents, pkg.currency)}/month`;
  }
  if (pkg.allow_installments && pkg.installment_count && pkg.installment_amount_cents) {
    return `${pkg.installment_count} × ${fmt(pkg.installment_amount_cents, pkg.currency)}`;
  }
  if (pkg.total_price_cents) return fmt(pkg.total_price_cents, pkg.currency);
  return '—';
}

function pkgDuration(pkg: PublicPackage): string {
  if (!pkg.duration_value || !pkg.duration_unit) return '';
  return `${pkg.duration_value} ${pkg.duration_unit}`;
}

// ── Stripe payment form inside package pay step ────────────────────────────────

function PackagePayForm({
  clientSecret,
  amountCents,
  currency,
  packageName,
  onSuccess,
  onError,
}: {
  clientSecret: string;
  amountCents: number;
  currency: string;
  packageName: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Card element not found');
      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card },
      });
      if (error) throw new Error(error.message || 'Payment failed');
      onSuccess();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <form onSubmit={handlePay} className="space-y-4">
      <div className="p-4 bg-primary-900/30 border border-primary-700/40 rounded-xl">
        <p className="text-xs text-primary-400 font-medium uppercase tracking-wide mb-1">Payment Due</p>
        <p className="text-white font-semibold">{packageName}</p>
        <p className="text-primary-200 text-sm mt-0.5">{fmt(amountCents, currency)}</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1.5">Card details</label>
        <div className="border border-dark-border rounded-lg p-3 bg-dark-canvas">
          <CardElement options={{
            style: {
              base: { color: '#e5e7eb', fontSize: '14px', '::placeholder': { color: '#6b7280' } },
              invalid: { color: '#f87171' },
            },
          }} />
        </div>
      </div>
      <button
        type="submit"
        disabled={paying || !stripe}
        className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {paying ? 'Processing…' : `Pay ${fmt(amountCents, currency)}`}
      </button>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CoachBookingPage() {
  const { coachId } = useParams<{ coachId: string }>();

  const [step, setStep] = useState<Step>('loading');
  const [info, setInfo] = useState<BookingInfo | null>(null);

  // Session booking state
  const [selectedEventType, setSelectedEventType] = useState<PublicEventType | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Shared client info
  const [clientEmail, setClientEmail] = useState('');
  const [clientFirstName, setClientFirstName] = useState('');
  const [clientLastName, setClientLastName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [sessionSubject, setSessionSubject] = useState('');
  const [isNewClient, setIsNewClient] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [meetUrl, setMeetUrl] = useState('');

  // Session payment state (enforce_payment_sessions)
  const [sessionPayResult, setSessionPayResult] = useState<{ clientSecret: string; publishableKey: string; accountId: string; priceCents: number; currency: string } | null>(null);

  // Package booking state
  const [selectedPkg, setSelectedPkg] = useState<PublicPackage | null>(null);
  const [startDate, setStartDate] = useState('');
  const [enrollResult, setEnrollResult] = useState<EnrollResult | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [payError, setPayError] = useState('');

  // Earliest valid start date
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!coachId) { setStep('not_found'); return; }
    fetch(`${config.apiBaseUrl}/book/${coachId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: BookingInfo) => {
        setInfo(data);
        setStep('picker');
      })
      .catch(() => setStep('not_found'));
  }, [coachId]);

  // Session booking handlers
  const handleEventTypeSelect = (et: PublicEventType) => {
    setSelectedEventType(et);
    setStep('slots');
  };

  const handleSlotSelect = (slot: string) => {
    setSelectedSlot(slot);
    setStep('confirm');
  };

  const handleSessionConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot || !selectedEventType || !coachId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/book/${coachId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type_id: selectedEventType.id,
          slot_time: selectedSlot,
          client_email: clientEmail,
          client_name: `${clientFirstName} ${clientLastName}`.trim(),
          client_phone: clientPhone,
          meeting_name: sessionSubject.trim() || undefined,
          invitee_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking failed');
      setMeetUrl(data.meeting_url || '');
      setIsNewClient(data.is_new_client || false);
      if (data.client_secret && data.stripe_publishable_key) {
        const opts = data.stripe_account_id ? { stripeAccount: data.stripe_account_id } : undefined;
        setStripePromise(loadStripe(data.stripe_publishable_key, opts));
        setSessionPayResult({
          clientSecret: data.client_secret,
          publishableKey: data.stripe_publishable_key,
          accountId: data.stripe_account_id || '',
          priceCents: data.price_cents || 0,
          currency: data.currency || 'usd',
        });
        setStep('session_pay');
      } else {
        setStep('confirmed');
      }
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Package booking handlers
  const handlePkgSelect = (pkg: PublicPackage) => {
    setSelectedPkg(pkg);
    setStartDate(today);
    setSubmitError('');
    setStep('pkg_details');
  };

  const handlePkgEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPkg || !coachId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/book/${coachId}/packages/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: selectedPkg.id,
          client_email: clientEmail,
          client_name: `${clientFirstName} ${clientLastName}`.trim(),
          client_phone: clientPhone,
          start_date: startDate,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Enrollment failed');
      setEnrollResult(data);
      setIsNewClient(data.is_new_client || false);

      if (data.client_secret && data.stripe_publishable_key) {
        const opts = data.stripe_account_id ? { stripeAccount: data.stripe_account_id } : undefined;
        setStripePromise(loadStripe(data.stripe_publishable_key, opts));
        setStep('pkg_pay');
      } else {
        // Offline / non-Stripe payment
        setStep('pkg_confirmed');
      }
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Nav = () => (
    <nav className="bg-dark-canvas border-b border-dark-border px-6 py-4 flex-shrink-0">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-8 h-8 bg-gradient-to-br from-fuchsia-400 to-violet-500 rounded-lg flex items-center justify-center shadow-md">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">CoachSight</span>
        </Link>
      </div>
    </nav>
  );

  const Footer = () => (
    <footer className="bg-dark-canvas border-t border-dark-border py-3 text-center text-dark-muted text-sm flex-shrink-0">
      &copy; {new Date().getFullYear()} CoachSight. All rights reserved.
    </footer>
  );

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-dark-canvas flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400" />
      </div>
    );
  }

  if (step === 'not_found') {
    return (
      <div className="min-h-screen bg-dark-canvas flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-white text-lg font-semibold mb-2">Booking link not found</p>
          <p className="text-dark-muted text-sm mb-6">This link may be invalid or expired.</p>
          <Link to="/" className="text-primary-400 hover:underline text-sm">← Back to home</Link>
        </div>
      </div>
    );
  }

  // ── Session confirmed ──────────────────────────────────────────────────────────

  if (step === 'confirmed') {
    return (
      <div className="min-h-screen bg-dark-canvas flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md bg-dark-card rounded-2xl shadow-2xl border border-dark-border p-8 text-center">
            <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">You're booked!</h2>
            {selectedEventType && <p className="text-dark-muted mb-1">{selectedEventType.name}</p>}
            {selectedSlot && (
              <p className="text-white font-medium mb-4">
                {new Date(selectedSlot).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                {' · '}
                {new Date(selectedSlot).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </p>
            )}
            <p className="text-sm text-dark-muted mb-2">
              A calendar invite has been sent to <span className="text-white">{clientEmail}</span>.
            </p>
            {isNewClient && (
              <p className="text-sm text-primary-300 mb-6">
                Check your inbox — we've sent you login details for your CoachSight account where you can track your sessions and action items.
              </p>
            )}
            {meetUrl && (
              <a href={meetUrl} target="_blank" rel="noopener noreferrer"
                className="inline-block mb-4 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors">
                Join Session
              </a>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Package confirmed ──────────────────────────────────────────────────────────

  if (step === 'pkg_confirmed') {
    const isStripe = enrollResult?.payment_method === 'stripe';
    return (
      <div className="min-h-screen bg-dark-canvas flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md bg-dark-card rounded-2xl shadow-2xl border border-dark-border p-8 text-center">
            <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {isStripe ? 'Payment confirmed!' : 'Enrollment confirmed!'}
            </h2>
            <p className="text-dark-muted mb-1">{enrollResult?.package_name}</p>
            <p className="text-sm text-dark-muted mb-4">
              Enrolled as <span className="text-white">{clientEmail}</span>.
            </p>
            {!isStripe && (
              <p className="text-sm text-primary-300 mb-4">
                Your coach will be in touch regarding payment.
              </p>
            )}
            {isNewClient && (
              <p className="text-sm text-primary-300">
                Check your inbox — we've sent you login details for your CoachSight account.
              </p>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Session pay (Stripe — enforce_payment_sessions) ───────────────────────────

  if (step === 'session_pay' && sessionPayResult && stripePromise) {
    return (
      <div className="min-h-screen bg-dark-canvas flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md space-y-5">
            <Elements stripe={stripePromise}>
              <PackagePayForm
                clientSecret={sessionPayResult.clientSecret}
                amountCents={sessionPayResult.priceCents}
                currency={sessionPayResult.currency}
                packageName={selectedEventType?.name || 'Session'}
                onSuccess={() => setStep('confirmed')}
                onError={(msg) => setPayError(msg)}
              />
            </Elements>
            {payError && <p className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{payError}</p>}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Package pay (Stripe) ───────────────────────────────────────────────────────

  if (step === 'pkg_pay' && enrollResult?.client_secret && stripePromise) {
    return (
      <div className="min-h-screen bg-dark-canvas flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md space-y-5">
            <Elements stripe={stripePromise}>
              <PackagePayForm
                clientSecret={enrollResult.client_secret}
                amountCents={enrollResult.amount_cents}
                currency={enrollResult.currency}
                packageName={enrollResult.package_name}
                onSuccess={() => setStep('pkg_confirmed')}
                onError={(msg) => setPayError(msg)}
              />
            </Elements>
            {payError && <p className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{payError}</p>}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Main layout (picker / slots / confirm / pkg_details) ──────────────────────

  return (
    <div className="min-h-screen bg-dark-canvas flex flex-col">
      <Nav />

      {/* Header */}
      <div className="bg-dark-sidebar border-b border-dark-border px-6 py-4 flex-shrink-0">
        <div className="max-w-2xl mx-auto">
          {step === 'picker' && info && (
            <div>
              <p className="text-white font-bold text-lg">Book with {info.coach_name}</p>
              <p className="text-xs text-gray-400 mt-0.5">Choose a session type or package below</p>
            </div>
          )}
          {(step === 'slots' || step === 'confirm') && selectedEventType && (
            <div className="flex items-center gap-3">
              <button onClick={() => setStep('picker')} className="text-gray-400 hover:text-gray-200 transition-colors">
                ←
              </button>
              <div>
                <p className="text-white font-semibold text-sm">{selectedEventType.name}</p>
                <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                  <ClockIcon className="w-3.5 h-3.5" />{selectedEventType.duration_minutes} min
                </p>
              </div>
            </div>
          )}
          {step === 'pkg_details' && selectedPkg && (
            <div className="flex items-center gap-3">
              <button onClick={() => setStep('picker')} className="text-gray-400 hover:text-gray-200 transition-colors">
                ←
              </button>
              <div>
                <p className="text-white font-semibold text-sm">{selectedPkg.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">Package enrollment</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Step: picker — sessions section + packages section */}
      {step === 'picker' && info && (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-5 py-8 space-y-6">

            {/* No offerings at all */}
            {info.event_types.length === 0 && info.packages.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-400 text-sm">No sessions or packages are currently available for booking.</p>
              </div>
            )}

            {/* ── Sessions container ─────────────────────────────────────── */}
            {info.event_types.length > 0 && (
              <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-dark-border">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Single Sessions</p>
                </div>
                <div className="p-3 space-y-2">
                  {info.event_types.map((et, i) => (
                    <button
                      key={et.id ?? et.uri ?? i}
                      onClick={() => handleEventTypeSelect(et)}
                      className="w-full text-left bg-dark-canvas border border-dark-border hover:border-primary-500 hover:bg-dark-hover rounded-xl px-5 py-4 transition-colors group flex items-center gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-white group-hover:text-primary-300 transition-colors">{et.name}</p>
                          {et.billing_type && et.billing_type !== 'billable' && (
                            <span className="text-[10px] bg-dark-hover border border-dark-border text-gray-400 rounded-full px-1.5 py-0.5 flex-shrink-0 capitalize">{et.billing_type === 'pro_bono' ? 'Pro Bono' : et.billing_type}</span>
                          )}
                          {et.billing_type === 'billable' && et.default_price_cents && (
                            <span className="text-[10px] text-primary-400 flex-shrink-0">{fmt(et.default_price_cents, et.default_currency || 'USD')}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5">
                          <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          {et.duration_minutes} min
                          {et.description && <span className="text-gray-500">· {et.description}</span>}
                        </p>
                      </div>
                      <ChevronRightIcon className="w-4 h-4 text-gray-500 group-hover:text-primary-400 flex-shrink-0 transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Packages container ────────────────────────────────────── */}
            {info.packages.length > 0 && (
              <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-dark-border">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Coaching Packages</p>
                </div>
                <div className="p-3 space-y-3">
                  {info.packages.map(pkg => {
                    const typeLabel =
                      pkg.package_type === 'ongoing_monthly' ? 'Monthly'
                      : pkg.package_type === 'session_bundle' ? 'Session Bundle'
                      : 'Fixed Duration';
                    const isStripePayment = pkg.payment_method === 'stripe';
                    return (
                      <button
                        key={pkg.id}
                        onClick={() => handlePkgSelect(pkg)}
                        className="w-full text-left bg-dark-canvas border border-dark-border hover:border-primary-500 hover:bg-dark-hover rounded-xl p-5 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Name + type pill */}
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <p className="text-sm font-semibold text-white group-hover:text-primary-300 transition-colors">{pkg.name}</p>
                              <span className="text-[10px] bg-dark-hover border border-dark-border text-gray-400 rounded-full px-2 py-0.5 flex-shrink-0">{typeLabel}</span>
                            </div>

                            {/* Price + duration row */}
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                              <span className="text-sm font-semibold text-primary-300">{pkgPriceSummary(pkg)}</span>
                              {pkgDuration(pkg) && (
                                <>
                                  <span className="text-gray-600">·</span>
                                  <span className="text-xs text-gray-400 flex items-center gap-1">
                                    <ClockIcon className="w-3.5 h-3.5" />{pkgDuration(pkg)}
                                  </span>
                                </>
                              )}
                            </div>

                            {/* Installment detail */}
                            {pkg.allow_installments && pkg.installment_count && pkg.installment_amount_cents && (
                              <p className="text-xs text-gray-500 mb-1">
                                {pkg.installment_count} payments of {fmt(pkg.installment_amount_cents, pkg.currency)}
                                {pkg.installment_frequency ? ` · ${pkg.installment_frequency}` : ''}
                              </p>
                            )}

                            {/* Payment method */}
                            {isStripePayment && (
                              <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                                <CreditCardIcon className="w-3.5 h-3.5" /> Pay online by card
                              </p>
                            )}

                            {/* Description */}
                            {pkg.description && (
                              <p className="text-xs text-gray-400 mt-1.5 leading-relaxed line-clamp-2">{pkg.description}</p>
                            )}
                          </div>
                          <ChevronRightIcon className="w-4 h-4 text-gray-500 group-hover:text-primary-400 flex-shrink-0 mt-0.5 transition-colors" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step: Slot picker */}
      {step === 'slots' && selectedEventType && selectedEventType.id && coachId && (
        <SlotPicker
          coachId={coachId}
          eventTypeId={selectedEventType.id}
          durationMinutes={selectedEventType.duration_minutes}
          onSelect={handleSlotSelect}
          onBack={() => setStep('picker')}
        />
      )}

      {/* Step: Session confirm */}
      {step === 'confirm' && selectedEventType && selectedSlot && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-5">
            <div className="p-4 bg-primary-900/30 border border-primary-700/40 rounded-xl">
              <p className="text-xs text-primary-400 font-medium uppercase tracking-wide mb-1">Session Details</p>
              <p className="text-white font-semibold">
                {new Date(selectedSlot).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              <p className="text-primary-200 text-sm mt-0.5">
                {new Date(selectedSlot).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                {' · '}{selectedEventType.duration_minutes} min · {selectedEventType.name}
              </p>
            </div>
            <form onSubmit={handleSessionConfirm} className="space-y-4">
              <ClientInfoFields
                clientFirstName={clientFirstName} setClientFirstName={setClientFirstName}
                clientLastName={clientLastName} setClientLastName={setClientLastName}
                clientEmail={clientEmail} setClientEmail={setClientEmail}
                clientPhone={clientPhone} setClientPhone={setClientPhone}
              />
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  What would you like to work on? <span className="text-dark-muted font-normal">(optional)</span>
                </label>
                <input type="text" value={sessionSubject} onChange={e => setSessionSubject(e.target.value)}
                  placeholder="e.g. Time management, goal review…"
                  className="w-full border border-dark-border rounded-lg p-2.5 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-2 focus:ring-primary-500 focus:outline-none" />
              </div>
              {submitError && <p className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{submitError}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {submitting ? 'Booking…' : 'Confirm Booking'}
              </button>
              <button type="button" onClick={() => setStep('slots')}
                className="w-full py-2 text-sm text-gray-400 hover:text-gray-200 text-center transition-colors">
                ← Back to time selection
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Step: Package details + enroll form */}
      {step === 'pkg_details' && selectedPkg && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-5">
            {/* Package summary */}
            <div className="p-4 bg-primary-900/30 border border-primary-700/40 rounded-xl">
              <p className="text-xs text-primary-400 font-medium uppercase tracking-wide mb-1">Package Details</p>
              <p className="text-white font-semibold">{selectedPkg.name}</p>
              <p className="text-primary-200 text-sm mt-0.5">{pkgPriceSummary(selectedPkg)}</p>
              {pkgDuration(selectedPkg) && <p className="text-xs text-gray-400 mt-0.5">{pkgDuration(selectedPkg)}</p>}
              {selectedPkg.allow_installments && selectedPkg.installment_count && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedPkg.installment_count} installments
                  {selectedPkg.installment_frequency ? ` · ${selectedPkg.installment_frequency}` : ''}
                </p>
              )}
              {selectedPkg.description && <p className="text-xs text-gray-400 mt-1">{selectedPkg.description}</p>}
            </div>

            <form onSubmit={handlePkgEnroll} className="space-y-4">
              <ClientInfoFields
                clientFirstName={clientFirstName} setClientFirstName={setClientFirstName}
                clientLastName={clientLastName} setClientLastName={setClientLastName}
                clientEmail={clientEmail} setClientEmail={setClientEmail}
                clientPhone={clientPhone} setClientPhone={setClientPhone}
              />
              <div className="flex flex-col items-center gap-1">
                <label className="text-xs font-medium text-gray-400 text-center">Start date</label>
                <input
                  type="date"
                  required
                  value={startDate}
                  min={today}
                  onChange={e => setStartDate(e.target.value)}
                  className="border border-dark-border rounded-lg px-3 py-2 text-sm bg-dark-canvas text-gray-200 focus:ring-2 focus:ring-primary-500 focus:outline-none w-44 [color-scheme:dark]"
                />
                {selectedPkg.package_type === 'ongoing_monthly' && (
                  <p className="text-xs text-gray-500">Monthly packages must start on the 1st of a month.</p>
                )}
              </div>
              {submitError && <p className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{submitError}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {submitting ? 'Processing…' : selectedPkg.payment_method === 'stripe' ? 'Continue to Payment' : 'Confirm Enrollment'}
              </button>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

// Shared client info fields component
function ClientInfoFields({
  clientFirstName, setClientFirstName,
  clientLastName, setClientLastName,
  clientEmail, setClientEmail,
  clientPhone, setClientPhone,
}: {
  clientFirstName: string; setClientFirstName: (v: string) => void;
  clientLastName: string; setClientLastName: (v: string) => void;
  clientEmail: string; setClientEmail: (v: string) => void;
  clientPhone: string; setClientPhone: (v: string) => void;
}) {
  return (
    <>
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-400 mb-1.5">First name</label>
          <input type="text" required value={clientFirstName} onChange={e => setClientFirstName(e.target.value)}
            placeholder="Jane"
            className="w-full border border-dark-border rounded-lg p-2.5 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-2 focus:ring-primary-500 focus:outline-none" />
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Last name</label>
          <input type="text" required value={clientLastName} onChange={e => setClientLastName(e.target.value)}
            placeholder="Smith"
            className="w-full border border-dark-border rounded-lg p-2.5 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-2 focus:ring-primary-500 focus:outline-none" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1.5">Email</label>
        <input type="email" required value={clientEmail} onChange={e => setClientEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full border border-dark-border rounded-lg p-2.5 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-2 focus:ring-primary-500 focus:outline-none" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1.5">Phone <span className="text-dark-muted font-normal">(optional)</span></label>
        <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)}
          placeholder="+1 555 000 0000"
          className="w-full border border-dark-border rounded-lg p-2.5 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-2 focus:ring-primary-500 focus:outline-none" />
      </div>
    </>
  );
}
